import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../api';
import { useAuth } from '../auth';

/** React Query key consumed by `queryInvalidationMap` on `TenantEntitlementsChanged`. */
export const ENTITLEMENTS_QUERY_KEY = ['entitlements'] as const;

export interface TenantEntitlements {
  tenantId: string;
  planId: string | null;
  planName: string | null;
  features: string[];
  version: number;
}

async function fetchTenantEntitlements(): Promise<TenantEntitlements> {
  const { data } = await apiClient.get<TenantEntitlements>('/api/me/entitlements');
  return data;
}

/**
 * Current tenant's effective feature set.
 * Fetch error → `features` null (fail-open, same as WPF).
 * Pending first fetch → `isResolved` false so the sidebar does not flash the full tree.
 */
export function useTenantEntitlements(): {
  features: ReadonlySet<string> | null;
  isResolved: boolean;
} {
  const { isAuthenticated } = useAuth();
  const query = useQuery({
    queryKey: ENTITLEMENTS_QUERY_KEY,
    queryFn: fetchTenantEntitlements,
    enabled: isAuthenticated,
    staleTime: 60_000,
    retry: 1,
  });

  const list = query.data?.features;
  const features = useMemo(() => (list ? new Set(list) : null), [list]);

  if (!isAuthenticated) {
    return { features: null, isResolved: true };
  }

  if (query.isPending && query.data === undefined) {
    return { features: null, isResolved: false };
  }

  if (query.isError || !list) {
    return { features: null, isResolved: true };
  }

  return { features, isResolved: true };
}
